import React, { useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";

import {
  ArrowUpRight,
  ChevronDown,
} from "lucide-react";

import { sendEnquiry } from "../../utils/enquiryEmailService.js";

import "../../App.css";

/* ============================================================
   SERVICE DATA
============================================================ */

const serviceData = {
  "Software Development": [
    "Custom Software Development",
    "Mobile App Development",
    "Web Development",
    "UI/UX Design",
    "CRM Solutions",
    "MLM Software",
  ],

  "API Services": [
    "WhatsApp API Services",
    "SMS OTP Services",
  ],

  "Legal Services": [
    "Business Registration",
    "Company Registration",
    "Taxation Services",
    "ISO Certification",
    "IPR Services",
  ],
};

/* ============================================================
   CUSTOM SERVICE DROPDOWN
============================================================ */

const ServiceDropdown = ({
  value,
  onChange,
  options,
  placeholder = "Select a service",
  error,
  onOpen,
  onClose,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  /* ==========================================================
     TOGGLE DROPDOWN
  ========================================================== */

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    if (isOpen) {
      setIsOpen(false);
      onClose?.();
    } else {
      onOpen?.();
      setIsOpen(true);
    }
  };

  /* ==========================================================
     SELECT OPTION
  ========================================================== */

  const handleSelect = (option) => {
    if (disabled) {
      return;
    }

    onChange(option);

    setIsOpen(false);

    onClose?.();
  };

  /* ==========================================================
     CLOSE OUTSIDE
  ========================================================== */

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (!event.target.closest("[data-service-dropdown]")) {
        setIsOpen(false);
        onClose?.();
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [isOpen, onClose]);

  /* ==========================================================
     CLOSE WHEN DISABLED
  ========================================================== */

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
      onClose?.();
    }
  }, [disabled, isOpen, onClose]);

  return (
    <div
      className="relative w-full"
      data-service-dropdown
    >
      {/* ======================================================
          DROPDOWN BUTTON
      ====================================================== */}

      <motion.button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={!disabled && isOpen}
        whileTap={
          !disabled
            ? {
                scale: 0.995,
              }
            : undefined
        }
        transition={{
          duration: 0.15,
        }}
        className={`
          group
          flex
          h-[43px]
          w-full
          items-center
          justify-between
          rounded-lg
          border
          px-3
          text-left
          text-[13px]
          outline-none
          transition-all
          duration-200

          sm:h-[47px]
          sm:px-4
          sm:text-sm

          ${
            error
              ? `
                border-red-500/60
                bg-red-500/[0.025]
                focus:border-red-500/80
              `
              : disabled
              ? `
                border-[var(--border-primary)]
                bg-[var(--card-bg)]
                cursor-default
                opacity-80
              `
              : isOpen
              ? `
                border-[var(--brand-primary)]
                bg-[var(--bg-tertiary)]
                shadow-[0_0_0_3px_rgba(249,115,22,0.06)]
              `
              : `
                border-[var(--border-primary)]
                bg-[var(--card-bg)]
                hover:border-[var(--brand-primary)]/50
                hover:bg-[var(--bg-tertiary)]
              `
          }
        `}
      >
        {/* ====================================================
            SELECTED VALUE
        ==================================================== */}

        <span
          className={`
            min-w-0
            truncate
            transition-colors
            duration-200

            ${
              value
                ? `
                  font-semibold
                  text-[var(--text-primary)]
                `
                : `
                  font-normal
                  text-[var(--text-muted)]
                `
            }
          `}
        >
          {value || placeholder}
        </span>

        {/* ====================================================
            ARROW
        ==================================================== */}

        {!disabled && (
          <ChevronDown
            className={`
              h-4
              w-4
              shrink-0
              text-[var(--text-muted)]
              transition-all
              duration-300
              ease-out

              ${
                isOpen
                  ? `
                    rotate-180
                    text-[var(--brand-primary)]
                  `
                  : `
                    rotate-0
                  `
              }
            `}
            strokeWidth={2}
          />
        )}
      </motion.button>

      {/* ======================================================
          DROPDOWN OPTIONS
      ====================================================== */}

      <AnimatePresence initial={false}>
        {!disabled && isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: -8,
              scale: 0.985,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: -6,
              scale: 0.985,
            }}
            transition={{
              duration: 0.2,
              ease: [0.22, 1, 0.36, 1],
            }}
            role="listbox"
            className="
              service-dropdown-scrollbar

              absolute
              left-0
              right-0
              top-[calc(100%+6px)]
              z-[100]

              max-h-[42vh]
              overflow-y-auto

              rounded-lg
              border
              border-[var(--border-primary)]
              bg-[var(--card-bg)]
              p-1
              shadow-[0_18px_50px_var(--shadow-color)]
              backdrop-blur-xl

              sm:max-h-60
            "
          >
            <div className="space-y-0.5">
              {options.map((option, index) => {
                const selected = value === option;

                return (
                  <motion.button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => handleSelect(option)}
                    initial={{
                      opacity: 0,
                      y: -4,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.16,
                      delay: index * 0.025,
                      ease: "easeOut",
                    }}
                    whileTap={{
                      scale: 0.985,
                    }}
                    className={`
                      flex
                      w-full
                      items-center
                      rounded-md
                      px-3
                      py-2.5
                      text-left
                      text-[13px]
                      outline-none
                      transition-all
                      duration-200
                      ease-out

                      sm:text-sm

                      ${
                        selected
                          ? `
                            bg-[var(--brand-primary)]
                            font-semibold
                            text-black
                            shadow-sm
                            hover:bg-[var(--brand-hover)]
                          `
                          : `
                            font-normal
                            text-[var(--text-secondary)]

                            hover:bg-[var(--bg-tertiary)]
                            hover:text-[var(--text-primary)]

                            focus:bg-[var(--bg-tertiary)]
                            focus:text-[var(--text-primary)]

                            active:bg-[var(--brand-primary)]/[0.10]
                          `
                      }
                    `}
                  >
                    <span className="truncate">
                      {option}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   CONTACT FORM
============================================================ */

const ContactForm = ({
  onSuccess,
  initialService = "",
  initialSubService = "",
  lockService = false,
  lockSubService = false,
}) => {
  /* ==========================================================
     AUTOMATIC LOCKING
  ========================================================== */

  const isServiceLocked =
    lockService || Boolean(initialService);

  const isSubServiceLocked =
    lockSubService || Boolean(initialSubService);

  /* ==========================================================
     INITIAL FORM DATA
  ========================================================== */

  const getInitialFormData = () => ({
    name: "",
    email: "",
    phone: "",
    serviceTitle: initialService || "",
    subService: initialSubService || "",
    message: "",
  });

  const [formData, setFormData] = useState(
    getInitialFormData
  );

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  const [serviceDropdownOpen, setServiceDropdownOpen] =
    useState(false);

  /* ==========================================================
     SYNC FORM WHEN MODAL SOURCE CHANGES
  ========================================================== */

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      serviceTitle: initialService || "",
      subService: initialSubService || "",
    }));

    setErrors({});

    setServiceDropdownOpen(false);
  }, [initialService, initialSubService]);

  /* ==========================================================
     VALIDATION
  ========================================================== */

  const validate = () => {
    const newErrors = {};

    /* ========================================================
       NAME
    ======================================================== */

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required.";
    }

    /* ========================================================
       EMAIL
    ======================================================== */

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email =
        "Enter a valid email address.";
    }

    /* ========================================================
       PHONE
    ======================================================== */

    if (!formData.phone.trim()) {
      newErrors.phone =
        "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone =
        "Enter a valid 10-digit phone number.";
    }

    /* ========================================================
       SERVICE
    ======================================================== */

    if (!formData.serviceTitle) {
      newErrors.serviceTitle =
        "Please select a service.";
    }

    /* ========================================================
       SUB SERVICE
    ======================================================== */

    if (
      formData.serviceTitle &&
      serviceData[formData.serviceTitle]?.length > 0 &&
      !formData.subService
    ) {
      newErrors.subService =
        "Please select a sub-service.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ==========================================================
     NORMAL INPUT CHANGE
  ========================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /* ==========================================================
     PHONE CHANGE
  ========================================================== */

  const handlePhoneChange = (e) => {
    const value = e.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));

    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  };

  /* ==========================================================
     SERVICE CHANGE
  ========================================================== */

  const handleServiceChange = (service) => {
    if (isServiceLocked) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      serviceTitle: service,
      subService: "",
    }));

    setErrors((prev) => ({
      ...prev,
      serviceTitle: "",
      subService: "",
    }));
  };

  /* ==========================================================
     SUB SERVICE CHANGE
  ========================================================== */

  const handleSubServiceChange = (subService) => {
    if (isSubServiceLocked) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      subService,
    }));

    if (errors.subService) {
      setErrors((prev) => ({
        ...prev,
        subService: "",
      }));
    }
  };

  /* ==========================================================
     SERVICE DROPDOWN OPEN
  ========================================================== */

  const handleServiceOpen = () => {
    if (isServiceLocked) {
      return;
    }

    setServiceDropdownOpen(true);
  };

  /* ==========================================================
     SERVICE DROPDOWN CLOSE
  ========================================================== */

  const handleServiceClose = () => {
    setServiceDropdownOpen(false);
  };

  /* ==========================================================
     RESET FORM
  ========================================================== */

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      serviceTitle: initialService || "",
      subService: initialSubService || "",
      message: "",
    });

    setErrors({});

    setServiceDropdownOpen(false);
  };

  /* ==========================================================
     SUBMIT
  ========================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const result = await sendEnquiry({
        ...formData,
        experience: "",
        skills: "",
        resume: "",
      });

      if (result.success) {
        resetForm();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        alert(
          "Unable to send enquiry. Please try again."
        );
      }
    } catch (error) {
      console.error(
        "Enquiry submission error:",
        error
      );

      alert(
        "Failed to send enquiry. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     INPUT CLASSES
  ========================================================== */

  const getInputClasses = (field) => `
    h-[43px]
    w-full
    rounded-lg
    border
    bg-[var(--card-bg)]
    border-[var(--border-primary)]
    px-3
    text-[13px]
    text-[var(--text-primary)]
    placeholder:text-[var(--text-muted)]
    outline-none
    transition-all
    duration-200

    sm:h-[47px]
    sm:px-4
    sm:text-sm

    [&:-webkit-autofill]:bg-[var(--card-bg)]
    [&:-webkit-autofill]:[-webkit-text-fill-color:var(--text-primary)]
    [&:-webkit-autofill]:[box-shadow:inset_0_0_0_1000px_var(--card-bg)]
    [&:-webkit-autofill]:[caret-color:var(--text-primary)]
    [&:-webkit-autofill]:transition-[background-color]
    [&:-webkit-autofill]:duration-[5000s]

    ${
      errors[field]
        ? `
          border-red-500/60
          focus:border-red-500/80
          focus:ring-2
          focus:ring-red-500/[0.06]
        `
        : `
          hover:border-[var(--brand-primary)]/50
          hover:bg-[var(--bg-tertiary)]

          focus:border-[var(--brand-primary)]
          focus:bg-[var(--bg-tertiary)]
          focus:ring-2
          focus:ring-orange-500/[0.06]
        `
    }
  `;

  /* ==========================================================
     TEXTAREA CLASSES
  ========================================================== */

  const textareaClasses = `
    min-h-[78px]
    w-full
    resize-none
    rounded-lg
    border
    border-[var(--border-primary)]
    bg-[var(--card-bg)]
    px-3
    py-2.5
    text-[13px]
    leading-5
    text-[var(--text-primary)]
    placeholder:text-[var(--text-muted)]
    outline-none
    transition-all
    duration-200

    hover:border-[var(--brand-primary)]/50
    hover:bg-[var(--bg-tertiary)]

    focus:border-[var(--brand-primary)]
    focus:bg-[var(--bg-tertiary)]
    focus:ring-2
    focus:ring-orange-500/[0.06]

    [&:-webkit-autofill]:bg-[var(--card-bg)]
    [&:-webkit-autofill]:[-webkit-text-fill-color:var(--text-primary)]
    [&:-webkit-autofill]:[box-shadow:inset_0_0_0_1000px_var(--card-bg)]

    sm:min-h-[92px]
    sm:px-4
    sm:py-2.5
    sm:text-sm
  `;

  /* ==========================================================
     SUB SERVICE OPTIONS
  ========================================================== */

  const subServices = formData.serviceTitle
    ? serviceData[formData.serviceTitle] || []
    : [];

  /* ==========================================================
     LABEL CLASSES
  ========================================================== */

  const labelClasses = `
    mb-1.5
    block
    text-[9px]
    font-bold
    uppercase
    tracking-[0.14em]
    text-[var(--text-muted)]

    sm:text-[10px]
  `;

  /* ==========================================================
     RETURN
  ========================================================== */

  return (
    <form
      onSubmit={handleSubmit}
      className="
        w-full
        min-w-0
      "
    >
      <div
        className="
          w-full
          space-y-2.5

          sm:space-y-3
        "
      >
        {/* ====================================================
            FULL NAME
        ==================================================== */}

        <div className="w-full">
          <label
            htmlFor="contact-name"
            className={labelClasses}
          >
            Full Name
          </label>

          <input
            id="contact-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            autoComplete="name"
            className={getInputClasses("name")}
          />

          {errors.name && (
            <p
              className="
                mt-1
                text-[9px]
                leading-4
                text-red-400
              "
            >
              {errors.name}
            </p>
          )}
        </div>

        {/* ====================================================
            EMAIL
        ==================================================== */}

        <div className="w-full">
          <label
            htmlFor="contact-email"
            className={labelClasses}
          >
            Email Address
          </label>

          <input
            id="contact-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            autoComplete="email"
            className={getInputClasses("email")}
          />

          {errors.email && (
            <p
              className="
                mt-1
                text-[9px]
                leading-4
                text-red-400
              "
            >
              {errors.email}
            </p>
          )}
        </div>

        {/* ====================================================
            PHONE
        ==================================================== */}

        <div className="w-full">
          <label
            htmlFor="contact-phone"
            className={labelClasses}
          >
            Phone Number
          </label>

          <input
            id="contact-phone"
            type="tel"
            name="phone"
            inputMode="numeric"
            maxLength={10}
            value={formData.phone}
            onChange={handlePhoneChange}
            placeholder="Enter your 10-digit phone number"
            autoComplete="tel"
            className={getInputClasses("phone")}
          />

          {errors.phone && (
            <p
              className="
                mt-1
                text-[9px]
                leading-4
                text-red-400
              "
            >
              {errors.phone}
            </p>
          )}
        </div>

        {/* ====================================================
            MAIN SERVICE
        ==================================================== */}

        <div className="w-full">
          <label
            htmlFor="contact-service"
            className={labelClasses}
          >
            Select Service
          </label>

          <ServiceDropdown
            value={formData.serviceTitle}
            onChange={handleServiceChange}
            options={Object.keys(serviceData)}
            placeholder="Select a service"
            error={errors.serviceTitle}
            onOpen={handleServiceOpen}
            onClose={handleServiceClose}
            disabled={isServiceLocked}
          />

          {errors.serviceTitle && (
            <p
              className="
                mt-1
                text-[9px]
                leading-4
                text-red-400
              "
            >
              {errors.serviceTitle}
            </p>
          )}
        </div>

        {/* ====================================================
            SUB SERVICE
        ==================================================== */}

        <AnimatePresence initial={false}>
          {formData.serviceTitle &&
            subServices.length > 0 && (
              <motion.div
                initial={{
                  opacity: 0,
                  height: 0,
                  y: -4,
                }}
                animate={{
                  opacity: 1,
                  height: "auto",
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  height: 0,
                  y: -4,
                }}
                transition={{
                  duration: 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="
                  w-full
                  overflow-visible
                "
              >
                <label
                  htmlFor="contact-sub-service"
                  className={labelClasses}
                >
                  Select Sub-Service
                </label>

                <ServiceDropdown
                  value={formData.subService}
                  onChange={handleSubServiceChange}
                  options={subServices}
                  placeholder="Select a sub-service"
                  error={errors.subService}
                  disabled={isSubServiceLocked}
                />

                {errors.subService && (
                  <p
                    className="
                      mt-1
                      text-[9px]
                      leading-4
                      text-red-400
                    "
                  >
                    {errors.subService}
                  </p>
                )}
              </motion.div>
            )}
        </AnimatePresence>

        {/* ====================================================
            MESSAGE
        ==================================================== */}

        <div className="w-full">
          <label
            htmlFor="contact-message"
            className={labelClasses}
          >
            Message
          </label>

          <textarea
            id="contact-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us briefly about your requirement..."
            rows={3}
            maxLength={1000}
            className={textareaClasses}
          />

          <div className="mt-1 flex justify-end">
            <span
              className="
                text-[8px]
                text-[var(--text-muted)]

                sm:text-[9px]
              "
            >
              {formData.message.length}/1000
            </span>
          </div>
        </div>
      </div>

      {/* ======================================================
          SUBMIT BUTTON
      ====================================================== */}

      <div className="mt-3 sm:mt-0">
        <button
          type="submit"
          disabled={loading}
          className="
            group
            relative
            inline-flex
            w-full
            items-center
            justify-center
            overflow-hidden
            rounded-full
            border
            border-[var(--brand-primary)]
            bg-[var(--brand-primary)]
            px-4
            py-2
            font-semibold
            text-black
            shadow-[0_8px_25px_rgba(249,115,22,0.12)]
            transition-all
            duration-300

            hover:scale-[1.015]
            hover:shadow-[0_10px_30px_rgba(249,115,22,0.18)]

            active:scale-[0.98]

            disabled:cursor-not-allowed
            disabled:opacity-60

            focus:outline-none
            focus-visible:ring-2
            focus-visible:ring-orange-500/40
            focus-visible:ring-offset-2
            focus-visible:ring-offset-[var(--bg-primary)]

            sm:w-auto
            sm:px-5
            sm:py-2.5
          "
        >
          {/* ==================================================
              HOVER BACKGROUND
          ================================================== */}

          <span
            aria-hidden="true"
            className="
              absolute
              inset-0
              w-0
              bg-[var(--bg-primary)]
              transition-all
              duration-300
              ease-out
              group-hover:w-full
            "
          />

          {/* ==================================================
              BUTTON CONTENT
          ================================================== */}

          <span
            className="
              relative
              z-10
              flex
              items-center
              gap-2
              text-xs

              sm:text-sm
            "
          >
            {loading ? (
              <>
                <span
                  className="
                    h-3.5
                    w-3.5
                    animate-spin
                    rounded-full
                    border-2
                    border-black
                    border-t-transparent
                  "
                />

                Sending...
              </>
            ) : (
              <>
                <span className="whitespace-nowrap">
                  Send Message
                </span>

                <span
                  className="
                    inline-flex
                    h-7
                    w-7
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[var(--bg-primary)]
                    text-[var(--text-primary)]
                    transition-all
                    duration-300

                    group-hover:translate-x-1
                    group-hover:rotate-45
                    group-hover:bg-[var(--brand-primary)]
                    group-hover:text-black
                  "
                >
                  <ArrowUpRight
                    className="h-3.5 w-3.5"
                  />
                </span>
              </>
            )}
          </span>
        </button>
      </div>
    </form>
  );
};

export default ContactForm;